import React, { useState, useEffect } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  LoaderCircle,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Smartphone,
  UsersRound,
  X,
} from 'lucide-react';
import { formatDateString, formatDateTimeString } from '../lib/utils';
import { useServiceUsers } from '../hooks/useServiceUsers';
import { ServiceUser, DeviceDetails } from '../models/ServiceUser';
import { serviceUserService, TestNotificationRequest } from '../services/serviceUserService';

const filterControlClassName = 'block h-11 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100';

export const RegisteredServiceUsers: React.FC = () => {
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'suspended' | 'pending'>('all');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ServiceUser | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ServiceUser | null>(null);
  const [deviceDetails, setDeviceDetails] = useState<DeviceDetails | null>(null);
  const [loadingDeviceDetails, setLoadingDeviceDetails] = useState(false);
  const [deviceDetailsError, setDeviceDetailsError] = useState<string | null>(null);
  const [sendingNotificationDeviceIndex, setSendingNotificationDeviceIndex] = useState<number | null>(null);
  const [notificationSuccess, setNotificationSuccess] = useState<string | null>(null);
  const [notificationError, setNotificationError] = useState<string | null>(null);

  // Use the API hook
  const {
    serviceUsers,
    loading,
    error,
    pagination,
    stats,
    refreshData,
    updateFilters,
  } = useServiceUsers({
    page: currentPage,
    limit: itemsPerPage,
    status: statusFilter,
    search: searchTerm,
  });

  // Debug logging removed for security

  // Update filters when search or status changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      
      updateFilters({
        page: 1, // Reset to first page when searching
        search: searchTerm,
        status: statusFilter,
        sortBy: sortConfig.key || undefined,
        sortOrder: sortConfig.key ? sortConfig.direction : undefined,
      });
      setCurrentPage(1);
    }, searchTerm ? 300 : 0); // Debounce search but not status changes

    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter, sortConfig, updateFilters]);

  // Update filters when pagination changes (but not when search/status changes)
  useEffect(() => {
    // Only update pagination if we're not in the middle of a search/status change
    if (currentPage > 1 || itemsPerPage !== 10) {
      updateFilters({
        page: currentPage,
        limit: itemsPerPage,
      });
    }
  }, [currentPage, itemsPerPage, updateFilters]);

  const handleSort = (key: keyof ServiceUser) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey: keyof ServiceUser) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />;
    }

    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />
      : <ArrowDown className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />;
  };

  const getStatusBadgeClass = (status: ServiceUser['profileStatus']) => {
    switch (status) {
      case 'active':
        return 'border-emerald-200 bg-emerald-50 text-emerald-700';
      case 'inactive':
        return 'border-amber-200 bg-amber-50 text-amber-700';
      case 'suspended':
        return 'border-red-200 bg-red-50 text-red-700';
      case 'pending':
        return 'border-blue-200 bg-blue-50 text-blue-700';
      default:
        return 'border-slate-200 bg-slate-50 text-slate-700';
    }
  };

  const getStatusText = (status: ServiceUser['profileStatus']) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getActiveBadgeClass = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const getActiveText = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const handleDeviceIconClick = async (user: ServiceUser) => {
    setSelectedUser(user);
    setIsDeviceModalOpen(true);
    setDeviceDetails(null);
    setDeviceDetailsError(null);
    setLoadingDeviceDetails(true);

    try {
      const response = await serviceUserService.getDeviceDetails(user.contactNumber);
      if (response.success) {
        setDeviceDetails(response.data);
      } else {
        setDeviceDetailsError(response.message || 'Failed to fetch device details');
      }
    } catch (error) {
      setDeviceDetailsError(error instanceof Error ? error.message : 'Failed to fetch device details');
    } finally {
      setLoadingDeviceDetails(false);
    }
  };

  const closeDeviceModal = () => {
    setIsDeviceModalOpen(false);
    setSelectedUser(null);
    setDeviceDetails(null);
    setDeviceDetailsError(null);
    setSendingNotificationDeviceIndex(null);
    setNotificationSuccess(null);
    setNotificationError(null);
  };

  const handleSendTestNotification = async (deviceIndex: number) => {
    if (!deviceDetails || !deviceDetails.devices || !deviceDetails.devices[deviceIndex]) {
      return;
    }

    const device = deviceDetails.devices[deviceIndex];
    const fcmToken = device.fcmToken;

    if (!fcmToken || fcmToken.trim() === '') {
      setNotificationError('FCM token is not available for this device');
      setTimeout(() => setNotificationError(null), 5000);
      return;
    }

    setSendingNotificationDeviceIndex(deviceIndex);
    setNotificationError(null);
    setNotificationSuccess(null);

    try {
      // Validate FCM token format
      const trimmedToken = fcmToken.trim();
      if (!trimmedToken || trimmedToken.length < 10) {
        setNotificationError('FCM token appears to be invalid or too short');
        setTimeout(() => setNotificationError(null), 5000);
        setSendingNotificationDeviceIndex(null);
        return;
      }

      // Build request payload
      const requestPayload: TestNotificationRequest = {
        fcmToken: trimmedToken,
        deviceType: device.deviceType,
        title: 'Test Notification',
        body: 'This is a test notification from the mechanic admin console',
        data: {
          testId: Date.now().toString(),
          source: 'admin-console',
          deviceId: device.deviceId,
        },
      };

      const response = await serviceUserService.sendTestNotification(requestPayload);

      if (response.success) {
        setNotificationSuccess(response.message || 'Test notification sent successfully');
        setTimeout(() => setNotificationSuccess(null), 5000);
      } else {
        setNotificationError(response.error || response.message || 'Failed to send test notification');
        setTimeout(() => setNotificationError(null), 5000);
      }
    } catch (error: unknown) {
      let errorMessage = 'An unexpected error occurred';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if there's additional error data from the API
        const errorWithData = error as Error & { errorData?: { error?: string; message?: string; details?: string } };
        if (errorWithData.errorData) {
          const apiError = errorWithData.errorData;
          if (apiError.details) {
            errorMessage = `${apiError.error || apiError.message || 'Error'}: ${apiError.details}`;
          } else if (apiError.error) {
            errorMessage = apiError.error;
          } else if (apiError.message) {
            errorMessage = apiError.message;
          }
        }
      }
      
      setNotificationError(errorMessage);
      setTimeout(() => setNotificationError(null), 5000);
    } finally {
      setSendingNotificationDeviceIndex(null);
    }
  };

  // Calculate display values
  const totalItems = pagination?.totalCount || 0;
  const totalPages = pagination?.totalPages || 1;
  const startIndex = pagination ? (pagination.currentPage - 1) * pagination.limit : 0;
  const endIndex = Math.min(startIndex + (pagination?.limit || 0), totalItems);
  const activeFilterCount = [Boolean(searchTerm.trim()), statusFilter !== 'all'].filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 px-5 py-6 shadow-lg shadow-slate-950/10 sm:px-6">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" aria-hidden="true" />
              Service user network
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Registered Service Users</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Monitor registered drivers, account status, recent activity, and connected devices.</p>
          </div>
          <button
            type="button"
            onClick={refreshData}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-semibold text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" role="list" aria-label="Service user summary">
        {/* Total Registered Card */}
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
                <UsersRound className="h-5 w-5 text-blue-700" aria-hidden="true" />
              </div>
            </div>
            <div className="mr-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Total registered</p>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {loading ? '…' : (stats?.total ?? serviceUsers.length).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-slate-500">Service user accounts</p>
            </div>
          </div>
        </article>

        {/* Active Users Card */}
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50">
                <CheckCircle2 className="h-5 w-5 text-emerald-700" aria-hidden="true" />
              </div>
            </div>
            <div className="mr-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Active users</p>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {loading ? '…' : (stats?.active ?? serviceUsers.filter(u => u.profileStatus === 'active').length).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-slate-500">Currently enabled</p>
            </div>
          </div>
        </article>

        {/* Month to Date Card */}
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50">
                <CalendarDays className="h-5 w-5 text-violet-700" aria-hidden="true" />
              </div>
            </div>
            <div className="mr-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Month to date</p>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {loading ? '…' : (stats?.monthToDate ?? 0).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-slate-500">New this month</p>
            </div>
          </div>
        </article>

      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-start justify-between gap-4 rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm" role="alert">
          <div className="flex items-start gap-3">
            <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
            <div><p className="text-sm font-semibold text-red-900">Unable to load service users</p><p className="mt-1 text-sm text-red-700">{error}</p></div>
          </div>
          <button
            type="button"
            onClick={refreshData}
            className="shrink-0 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            Try again
          </button>
        </div>
      )}

      {/* Data Table */}
      <div className="admin-table-shell">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><UsersRound className="h-[18px] w-[18px]" aria-hidden="true" /></span><div><h2 className="text-base font-semibold text-slate-950">Service user directory</h2><p className="mt-0.5 text-xs text-slate-500" aria-live="polite">Showing {totalItems === 0 ? 0 : startIndex + 1}–{Math.min(endIndex, totalItems)} of {totalItems} service users</p></div></div>
          <button type="button" onClick={() => setAreFiltersOpen(current => !current)} aria-expanded={areFiltersOpen} aria-controls="service-user-filters" className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areFiltersOpen || activeFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}><SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />Filters{activeFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{activeFilterCount}</span>}<ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" /></button>
        </div>
        <div id="service-user-filters" hidden={!areFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1"><label htmlFor="service-user-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search service users</label><div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" /><input id="service-user-search" type="search" placeholder="Name or contact number" className={`${filterControlClassName} pl-10`} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} /></div></div>
            <div className="sm:w-48"><label htmlFor="service-user-status" className="mb-1.5 block text-xs font-semibold text-slate-600">Status</label><select id="service-user-status" className={filterControlClassName} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option><option value="pending">Pending</option></select></div>
            <div className="sm:w-36"><label htmlFor="service-user-items-per-page" className="mb-1.5 block text-xs font-semibold text-slate-600">Rows</label><select id="service-user-items-per-page" className={filterControlClassName} value={itemsPerPage} onChange={(event) => { setItemsPerPage(Number(event.target.value)); setCurrentPage(1); }}><option value={5}>5 per page</option><option value={10}>10 per page</option><option value={25}>25 per page</option><option value={50}>50 per page</option></select></div>
          </div>
          {activeFilterCount > 0 && <div className="mt-4 flex justify-end"><button type="button" onClick={() => { setSearchTerm(''); setStatusFilter('all'); setCurrentPage(1); }} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><X className="mr-2 h-4 w-4" aria-hidden="true" />Clear filters</button></div>}
        </div>
        {loading && (
          <div className="flex flex-col items-center justify-center py-14">
            <LoaderCircle className="mb-3 h-8 w-8 animate-spin text-blue-600" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-slate-900">Loading service users…</h3>
            <p className="mt-1 text-sm text-slate-500">Fetching the latest account records.</p>
          </div>
        )}
        
        {!loading && (
          <div className="admin-table-scroll" role="region" aria-label="Registered service users data table" tabIndex={0}>
            <table className="admin-data-table min-w-max">
              <thead>
                <tr>
                  <th 
                    onClick={() => handleSort('profileStatus')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') handleSort('profileStatus');
                    }}
                    tabIndex={0}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {getSortIcon('profileStatus')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('fullName')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') handleSort('fullName');
                    }}
                    tabIndex={0}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Full Name</span>
                      {getSortIcon('fullName')}
                    </div>
                  </th>
                  <th>
                    Contact Number
                  </th>
                  <th 
                    onClick={() => handleSort('registeredOn')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') handleSort('registeredOn');
                    }}
                    tabIndex={0}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Registered On</span>
                      {getSortIcon('registeredOn')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('lastLogin')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') handleSort('lastLogin');
                    }}
                    tabIndex={0}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Login</span>
                      {getSortIcon('lastLogin')}
                    </div>
                  </th>
                  <th>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {serviceUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-14 text-center">
                      <div className="flex flex-col items-center">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                          <UsersRound className="h-6 w-6" aria-hidden="true" />
                        </div>
                        <h3 className="mb-1 text-sm font-semibold text-slate-900">No service users found</h3>
                        <p className="text-sm text-slate-500">Try adjusting your search criteria or filters.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  serviceUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="whitespace-nowrap">
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(user.profileStatus)}`}>
                          {getStatusText(user.profileStatus)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="text-sm font-semibold text-slate-900">{user.fullName}</div>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="text-sm text-slate-700">{user.contactNumber}</div>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="text-sm text-slate-700">{formatDateTimeString(user.registeredOn)}</div>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="text-sm text-slate-700">{formatDateString(user.lastLogin)}</div>
                      </td>
                      <td className="whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleDeviceIconClick(user)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          aria-label={`View device details for ${user.fullName}`}
                          title={`View device details for ${user.fullName}`}
                        >
                          <Smartphone className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-table-pagination">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="ml-3 inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-600">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex -space-x-px overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" aria-label="Pagination">
                {/* Previous Button */}
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex h-10 w-10 items-center justify-center border-r border-slate-200 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>

                {/* Page Numbers */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  // First page
                  if (startPage > 1) {
                    pages.push(
                      <button
                        type="button"
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className="relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-medium text-slate-400">
                          ...
                        </span>
                      );
                    }
                  }

                  // Visible pages
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        type="button"
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        aria-current={i === currentPage ? 'page' : undefined}
                        className={`relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-semibold ${
                          i === currentPage
                            ? 'z-10 bg-slate-900 text-white'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Last page
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-medium text-slate-400">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        type="button"
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}

                {/* Next Button */}
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex h-10 w-10 items-center justify-center text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Device Details Modal */}
      {isDeviceModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Device Details</h3>
                  {selectedUser && (
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedUser.fullName} - {selectedUser.contactNumber}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  {selectedUser && (
                    <button
                      onClick={async () => {
                        if (!deviceDetails) {
                          setNotificationError('Device details not loaded yet. Please wait.');
                          setTimeout(() => setNotificationError(null), 5000);
                          return;
                        }

                        // Try to find any available FCM token
                        let fcmToken: string | null = null;
                        let deviceType: 'ios' | 'android' | undefined = undefined;

                        // First try legacy token
                        if (deviceDetails.fcmToken) {
                          fcmToken = deviceDetails.fcmToken;
                        }
                        // Then try first device token
                        else if (deviceDetails.devices && deviceDetails.devices.length > 0 && deviceDetails.devices[0].fcmToken) {
                          fcmToken = deviceDetails.devices[0].fcmToken;
                          deviceType = deviceDetails.devices[0].deviceType;
                        }

                        if (!fcmToken) {
                          setNotificationError('No FCM token available for this user');
                          setTimeout(() => setNotificationError(null), 5000);
                          return;
                        }

                        setSendingNotificationDeviceIndex(-2); // Use -2 for header button
                        setNotificationError(null);
                        setNotificationSuccess(null);

                        try {
                          const trimmedToken = fcmToken.trim();
                          if (!trimmedToken || trimmedToken.length < 10) {
                            setNotificationError('FCM token appears to be invalid or too short');
                            setTimeout(() => setNotificationError(null), 5000);
                            setSendingNotificationDeviceIndex(null);
                            return;
                          }

                          const requestPayload: TestNotificationRequest = {
                            fcmToken: trimmedToken,
                            deviceType: deviceType,
                            title: 'Test Notification',
                            body: 'This is a test notification from the mechanic admin console',
                            data: {
                              testId: Date.now().toString(),
                              source: 'admin-console',
                              contactNumber: selectedUser.contactNumber,
                            },
                          };

                          const response = await serviceUserService.sendTestNotification(requestPayload);

                          if (response.success) {
                            setNotificationSuccess(response.message || 'Test notification sent successfully');
                            setTimeout(() => setNotificationSuccess(null), 5000);
                          } else {
                            setNotificationError(response.error || response.message || 'Failed to send test notification');
                            setTimeout(() => setNotificationError(null), 5000);
                          }
                        } catch (error: unknown) {
                          let errorMessage = 'An unexpected error occurred';
                          if (error instanceof Error) {
                            errorMessage = error.message;
                          }
                          setNotificationError(errorMessage);
                          setTimeout(() => setNotificationError(null), 5000);
                        } finally {
                          setSendingNotificationDeviceIndex(null);
                        }
                      }}
                      disabled={loadingDeviceDetails || sendingNotificationDeviceIndex === -2 || !deviceDetails || (!deviceDetails.fcmToken && (!deviceDetails.devices || deviceDetails.devices.length === 0 || !deviceDetails.devices[0].fcmToken))}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      type="button"
                      title={!deviceDetails ? 'Loading device details...' : (!deviceDetails.fcmToken && (!deviceDetails.devices || deviceDetails.devices.length === 0 || !deviceDetails.devices[0].fcmToken)) ? 'No FCM token available' : 'Send test notification'}
                    >
                      {sendingNotificationDeviceIndex === -2 ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          Send Test Notification
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={closeDeviceModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="max-h-[60vh] overflow-y-auto">
                {/* Notification Success/Error Messages */}
                {notificationSuccess && (
                  <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="ml-3">
                        <p className="text-sm text-green-800">{notificationSuccess}</p>
                      </div>
                    </div>
                  </div>
                )}
                {notificationError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{notificationError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {loadingDeviceDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-gray-600">Loading device details...</span>
                    </div>
                  </div>
                ) : deviceDetailsError ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{deviceDetailsError}</p>
                      </div>
                    </div>
                  </div>
                ) : deviceDetails ? (
                  <div className="space-y-4">
                    {/* Backward compatibility FCM Token */}
                    {deviceDetails.fcmToken && (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">FCM Token (Legacy)</h4>
                        <p className="text-xs text-gray-600 font-mono break-all">{deviceDetails.fcmToken}</p>
                      </div>
                    )}

                    {/* Devices List */}
                    {deviceDetails.devices && deviceDetails.devices.length > 0 ? (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Registered Devices ({deviceDetails.devices.length})
                        </h4>
                        <div className="space-y-3">
                          {deviceDetails.devices.map((device, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <div className="mb-4 pb-3 border-b border-gray-300">
                                <h5 className="text-sm font-semibold text-gray-900 capitalize">
                                  Device {index + 1} - {device.deviceType}
                                </h5>
                                <p className="text-xs text-gray-500 mt-1">
                                  {device.deviceModel || 'Unknown Model'} • {device.osVersion || 'Unknown OS'}
                                </p>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Device Type</p>
                                  <p className="text-sm text-gray-900 capitalize">{device.deviceType}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Device Model</p>
                                  <p className="text-sm text-gray-900">{device.deviceModel || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">OS Version</p>
                                  <p className="text-sm text-gray-900">{device.osVersion || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">App Version</p>
                                  <p className="text-sm text-gray-900">{device.appVersion || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Device ID</p>
                                  <p className="text-sm text-gray-900 font-mono break-all">{device.deviceId || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">FCM Token</p>
                                  <p className="text-sm text-gray-900 font-mono break-all text-xs">{device.fcmToken || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Registered At</p>
                                  <p className="text-sm text-gray-900">{formatDateString(device.registeredAt)}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Last Active</p>
                                  <p className="text-sm text-gray-900">{formatDateString(device.lastActiveAt)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex">
                          <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div className="ml-3">
                            <p className="text-sm text-yellow-800">No devices registered for this user.</p>
                            {deviceDetails.fcmToken && (
                              <p className="text-xs text-yellow-700 mt-1">Legacy FCM token is available above.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Modal Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={closeDeviceModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
